import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";

import { TCatalogEntities } from "types";
import Pipeline from "../Pipeline";
import { sortAsc } from "../helper";

import List from "../../List";
import Filter from "../../Filter";

import AufbauReducer from "./AufbauReducer";
import AufbauIndicator from "./AufbauIndicator";

interface IFilter {
  entities: TCatalogEntities;
  value: number[];
  dispatchFilter: any;
}

export const SideFilter: React.FC<IFilter> = ({
  entities = {},
  value = [],
  dispatchFilter = () => null,
}): JSX.Element | null => {
  const { formatMessage: t } = useIntl();

  if (!entities.aufbau) return null;

  const items = Object.values(entities.aufbau);
  const itemsCount = items.length;

  const setValueHandler = (value: any) => {
    // Check if all variants are selected
    const newValue = value.length === itemsCount ? [] : value;
    dispatchFilter({ type: "UPDATE_FILTER", payload: { aufbau: newValue } });
  };

  const options = items.map(({ id, name }) => ({
    value: id,
    title: t({ id: `aufbau.${id}.label`, defaultMessage: name }),
  }));

  const all = {
    title: t({ id: "common.alle", defaultMessage: "Alle" }),
  };

  const Component = (
    <Box sx={{ width: 1 }}>
      <List {...{ value, options, setValueHandler, all }} />
    </Box>
  );
  return (
    <Filter
      component={Component}
      title={
        <FormattedMessage id="filter.aufbau.label" defaultMessage="Aufbau" />
      }
    />
  );
};

export const SimpleFilter: React.FC<IFilter> = ({
  entities = {},
  value = [],
  dispatchFilter = () => null,
}): JSX.Element | null => {
  const { formatMessage: t } = useIntl();
  if (!entities.aufbau) return null;

  const items = Object.values(entities.aufbau);

  const setValueHandler = (event: SelectChangeEvent) => {
    // Check if all variants are selected
    const value = parseInt(event.target.value);
    const newValue = value === 0 ? [] : [value];
    dispatchFilter({
      type: "UPDATE_FILTER",
      payload: { aufbau: newValue },
    });
  };

  const options = items
    .map(({ id, name }) => ({
      value: id,
      title: t({ id: `aufbau.${id}.label`, defaultMessage: name }),
    }))
    .sort(sortAsc);

  const all = {
    title: t({ id: "common.alle", defaultMessage: "Alle" }),
  };

  return (
    <FormControl size="small">
      <InputLabel id="filter-aufbau-simple-label">
        <FormattedMessage id="filter.aufbau.label" defaultMessage="Aufbau" />
      </InputLabel>
      <Select
        labelId="filter-aufbau-simple-label"
        id="filter-aufbau-simple"
        value={value.length ? value[0].toString() : "0"}
        label={t({
          id: "filter.aufbau.label",
          defaultMessage: "Aufbau",
        })}
        onChange={setValueHandler}
      >
        <MenuItem value={0}>{all.title}</MenuItem>
        {options.map(({ value, title }) => (
          <MenuItem key={"aufbau-" + value} value={value}>
            {title}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

interface IContainer {
  Pipe?: any;
  variant?: string;
}

const Container: React.FC<IContainer> = ({
  Pipe = Pipeline,
  variant = "side",
}) => {
  const Filter = variant === "side" ? SideFilter : SimpleFilter;
  return (
    <Pipe
      {...{
        Component: Filter,
        type: "aufbau",
        defaultValue: [],
        Reducer: AufbauReducer,
        Indicator: AufbauIndicator,
      }}
    />
  );
};

export default Container;
